/**
 * Zustand store for OPL (Open Issue List) management.
 */

import { create } from 'zustand';
import * as oplApi from '@/api/opl';
import type {
  OplIssueDetail,
  OplIssueListItem,
  OplIssueQueryParams,
  OplOverviewResponse,
} from '@/types';

interface OplPagination {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface OplState {
  // Issue list
  issues: OplIssueListItem[];
  pagination: OplPagination;

  // Current issue
  currentIssue: OplIssueDetail | null;

  // Overview
  overview: OplOverviewResponse | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchIssues: (projectId: number, params?: OplIssueQueryParams) => Promise<void>;
  fetchIssue: (projectId: number, issueId: number) => Promise<void>;
  fetchOverview: (projectId: number) => Promise<void>;
  setCurrentIssue: (issue: OplIssueDetail | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialPagination: OplPagination = {
  count: 0,
  page: 1,
  pageSize: 20,
  totalPages: 0,
};

export const useOplStore = create<OplState>()((set) => ({
  issues: [],
  pagination: { ...initialPagination },
  currentIssue: null,
  overview: null,
  isLoading: false,
  error: null,

  fetchIssues: async (projectId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await oplApi.listIssues(projectId, params);
      set({
        issues: response.results,
        pagination: {
          count: response.count,
          page: response.page,
          pageSize: response.page_size,
          totalPages: response.total_pages,
        },
        isLoading: false,
      });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || '获取问题列表失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchIssue: async (projectId, issueId) => {
    set({ isLoading: true, error: null });
    try {
      const issue = await oplApi.getIssue(projectId, issueId);
      set({ currentIssue: issue, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || '获取问题详情失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchOverview: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const overview = await oplApi.getOverview(projectId);
      set({ overview, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || '获取问题总览失败';
      set({ error: message, isLoading: false });
    }
  },

  setCurrentIssue: (issue) => set({ currentIssue: issue }),

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      issues: [],
      pagination: { ...initialPagination },
      currentIssue: null,
      overview: null,
      isLoading: false,
      error: null,
    }),
}));
